﻿import { useState, useCallback } from 'react';
import { config } from '@/config/env';

export function useReport() {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const buildReportInput = (id: string, versione: number, ragsociale: string) => {
    const lang = sessionStorage.getItem('lang') || 'it';
    return {
      reportName: 'dativisita_' + lang + '.jrxml',
      fileName: (ragsociale || 'visita') + '_' + id,
      fileExtension: 'pdf',
      parameters: { site: config.SITE, lang, versione, id },
    };
  };

  const fetchReportBlob = async (inputws: any): Promise<Blob> => {
    const base = config.WSREST_TABATA.replace(/\/+$/, '');
    const token = sessionStorage.getItem('mT');
    const res = await fetch(base + '/lmeet/report/download', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify(inputws),
    });
    if (!res.ok) throw new Error('Report download failed: ' + res.status);
    const blob = await res.blob();
    return new Blob([blob], { type: 'application/pdf' });
  };

  const download = useCallback(async (id: string, versione: number, ragsociale: string) => {
    setDownloading(true);
    try {
      const inputws = buildReportInput(id, versione, ragsociale);
      const blob = await fetchReportBlob(inputws);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = inputws.fileName + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      console.error('Download error:', err);
      return false;
    } finally {
      setDownloading(false);
    }
  }, []);

  const preview = useCallback(async (id: string, versione: number, ragsociale: string): Promise<Blob | null> => {
    setPreviewing(true);
    try {
      const inputws = buildReportInput(id, versione, ragsociale);
      return await fetchReportBlob(inputws);
    } catch (err) {
      console.error('Preview error:', err);
      return null;
    } finally {
      setPreviewing(false);
    }
  }, []);

  const sendEmail = useCallback(async (id: string, versione: number, ragsociale: string, email: string) => {
    setSending(true);
    try {
      const inputws = { ...buildReportInput(id, versione, ragsociale), email };
      const base = config.WSREST_TABATA.replace(/\/+$/, '');
      const token = sessionStorage.getItem('mT');
      const res = await fetch(base + '/lmeet/report/sendemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(inputws),
      });
      if (!res.ok) throw new Error('Send email failed');
      return true;
    } catch (err) {
      console.error('Send email error:', err);
      return false;
    } finally {
      setSending(false);
    }
  }, []);

  return { download, preview, sendEmail, downloading, previewing, sending };
}
